const STORAGE_KEY = "tint_marketing_attribution"

// Google Ads click ids stay valid for ~90 days; older attribution is stale.
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000

const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"] as const
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const

type StoredAttribution = {
  capturedAt: number
  params: Record<string, string>
}

/**
 * Persists ad-click attribution (gclid/gbraid/wbraid + UTM) from the current
 * URL. Must run at app bootstrap, before auth/router redirects strip the query
 * string — PostHog initializes lazily and would otherwise never see the params.
 */
export const captureMarketingAttributionFromUrl = () => {
  try {
    const search = new URLSearchParams(window.location.search)
    const params: Record<string, string> = {}

    for (const key of [...CLICK_ID_PARAMS, ...UTM_PARAMS]) {
      const value = search.get(key)?.trim()
      if (value) params[key] = value
    }

    if (Object.keys(params).length === 0) return

    // Merge over the previous record so a later utm-only visit does not erase
    // a still-valid click id captured earlier.
    const stored: StoredAttribution = {
      capturedAt: Date.now(),
      params: { ...(readMarketingAttribution() ?? {}), ...params },
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage unavailable (private mode / blocked) — attribution is best-effort.
  }
}

export const readMarketingAttribution = (): Record<string, string> | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const stored = JSON.parse(raw) as StoredAttribution | null
    if (!stored || typeof stored !== "object" || !stored.params) return null

    if (
      !Number.isFinite(stored.capturedAt) ||
      Date.now() - stored.capturedAt > ATTRIBUTION_TTL_MS
    ) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }

    const entries = Object.entries(stored.params).filter(
      ([, value]) => typeof value === "string" && value.trim().length > 0
    )
    return entries.length > 0 ? Object.fromEntries(entries) : null
  } catch {
    return null
  }
}
